import { Injectable } from "@angular/core";

import { Observable } from "rxjs";

import { dateToDb, dbToDate, dbToInt, EntitetStatus } from "@dffedb/util";
import { InstallationHttpClient, ParametreForAktuelleInstallation } from "@e-forsyning/common/http";

import { DbNote } from "../model/db-note";
import { DbNoteBilag } from "../model/db-note-bilag";
import { Note, NoteOpgavestyringStatus } from "../model/note.model";
import { NoteHenvendelsestype } from "../model/note.model";

@Injectable({ providedIn: "root" })
export class NoteDbMapperService {
    constructor(private readonly http: InstallationHttpClient) {}

    public dbToHenvendelsestyper(data: any[]): NoteHenvendelsestype[] {
        const result = data.map((h: any) => this.dbToHenvendelsestype(h));
        return result;
    }

    public dbToHenvendelsestype(data: any): NoteHenvendelsestype {
        const result: NoteHenvendelsestype = {
            key: dbToInt(data.henvendelsestypeid),
            omraadeId: dbToInt(data.henvendelsestypeomraade),
            value: data.henvendelsestypebeskriv
        };
        return result;
    }

    public dbToOpgavestyringStatusser(data: any): NoteOpgavestyringStatus[] {
        const result = data.map((s: any) => this.dbToOpgavestyringStatus(s));
        return result;
    }

    public dbToOpgavestyringStatus(data: any): NoteOpgavestyringStatus {
        const result: NoteOpgavestyringStatus = {
            key: dbToInt(data.tilstandid),
            value: data.tilstandtekst
        };
        return result;
    }

    public getBilagUrlTemplate(): Observable<string> {
        return this.http.getUrl("api/notebilag/dokumentid/[dokumentid]/omraade/[omraadeid]", ParametreForAktuelleInstallation.true);
    }

    public dbToNoter(dbNoter: DbNote[], bilagUrlTemplate: string): Note[] {
        const result = dbNoter.map((dbNote) => this.dbToNote(dbNote, bilagUrlTemplate));
        return result;
    }

    public dbToNote(dbNote: DbNote, bilagUrlTemplate: string): Note {
        const fullyLoaded = dbNote.hasOwnProperty("henvendelsestype");
        const genererUrl = this.genererUrl;
        const result: Note = {
            get antalBilag(): number {
                return this.bilag.length;
            },
            bilag: dbNote.notebilag.map((b) => ({
                filNavn: b.filnavn,
                filStoerrelseKb: dbToInt(b.filstoerelse),
                noteBilagId: dbToInt(b.dokumentid),
                status: this.beregnStatus(b.status),
                get url(): string {
                    return genererUrl(bilagUrlTemplate, dbNote, b);
                }
            })),
            dato: dbToDate(dbNote.dato),
            fullyLoaded, // !!dbNote.henvendelsestype,
            henvendelseTypeId: dbToInt(dbNote.henvendelsestype),
            noteId: dbToInt(dbNote.id),
            omraadeId: dbToInt(dbNote.omraade),
            opgavestyring: {
                opfoelgAf: dbNote.opfoelger,
                opfoelgDato: dbToDate(dbNote.opfoelgdato),
                statusId: dbToInt(dbNote.tilstand),
                udfoertAf: dbNote.udfoertaf,
                udfoertDato: dbToDate(dbNote.udfoertdato)
            },
            overskrift: dbNote.overskrift,
            status: this.beregnStatus(dbNote.status),
            tekst: dbNote.notetekst.replace(/\ <br> /g, "\n") // FOF sender <br>-tags til os. Vi ønsker linebreaks.
        };
        return result;
    }

    public noterToDb(noter: Note[]): DbNote[] {
        const result = noter.map((note) => this.noteToDb(note));
        return result;
    }

    public noteToDb(note: Note): DbNote {
        const result: DbNote = {
            dato: dateToDb(note.dato),
            henvendelsestype: (note.henvendelseTypeId && note.henvendelseTypeId.toString()) || null,
            id: note.noteId.toString(),
            notebilag: note.bilag.map((b) => ({
                filnavn: b.filNavn,
                filstoerelse: b.filStoerrelseKb && b.filStoerrelseKb.toString(),
                dokumentid: b.noteBilagId && b.noteBilagId.toString(),
                status: b.status,
                fil: b.url
            })),
            notetekst: note.tekst, // Ingen konvertering, da FOF ønsker linebreaks (selvom den sender <br>-tags tilbage)
            omraade: note.omraadeId && note.omraadeId.toString(),
            opfoelgdato: dateToDb(note.opgavestyring.opfoelgDato),
            opfoelger: note.opgavestyring.opfoelgAf,
            overskrift: note.overskrift,
            status: note.status,
            tilstand: (note.opgavestyring.statusId && note.opgavestyring.statusId.toString()) || "",
            udfoertaf: note.opgavestyring.udfoertAf,
            udfoertdato: dateToDb(note.opgavestyring.udfoertDato)
        };
        return result;
    }

    private beregnStatus(status: EntitetStatus): EntitetStatus {
        if (status === null || status === undefined) {
            return EntitetStatus.Uaendret;
        }
        return status;
    }

    private genererUrl(bilagTemplateUrl: string, dbNote: DbNote, dbBilag: DbNoteBilag): string {
        if (dbBilag.status === EntitetStatus.Tilfoejet) {
            return dbBilag.fil;
        }

        const omraadeId = parseInt(dbNote.omraade, 10);
        const dokumentId = parseInt(dbBilag.dokumentid, 10);
        return bilagTemplateUrl.replace("[dokumentid]", dokumentId.toString()).replace("[omraadeid]", omraadeId.toString());
    }
}
